import { Component, Inject, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroupDirective, NgForm } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import type { ITransaction } from '../../models';
import {
  Alignment,
  ConfirmationService,
  FormBuilder,
  FormData,
  FormValidation,
  FormField,
  InputIconType,
  InputPrefixSuffixType,
  InputType,
  MonekColour,
  NotificationService,
  Size,
  VTService,
  ValidationPattern,
  OptionInputType,
  FormOption,
  MerchantService,
  CountryConvertService,
  ErrorReferenceService,
} from '@odin/odin-core';
import { VTPaymentResponse } from '../../models/vt-payment';
import { CurrencyPipe, getCurrencySymbol } from '@angular/common';
import { TransactionRecharge } from '../../models/transaction-recharge';
import type { AccountOption } from '../account-selector/account-selector.component';

export class RechargeModalData {
  constructor(public transaction: ITransaction) {}
}

/** Error when invalid control is dirty, touched, or submitted. */
export class AmountErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(
    control: FormControl | null,
    form: FormGroupDirective | NgForm | null,
  ): boolean {
    const isSubmitted = form && form.submitted;
    return !!(
      control &&
      control.invalid &&
      (control.dirty || control.touched || isSubmitted)
    );
  }
}

@Component({
  standalone: false,
  selector: 'odin-recharge-transaction-modal',
  templateUrl: './recharge-transaction-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./recharge-transaction-modal.component.scss'],
})
export class RechargeTransactionModalComponent {
  public rechargeForm: FormData<TransactionRecharge>;
  public amountMatcher = new AmountErrorStateMatcher();
  public processing: boolean = false;
  /** MonekID the recharge will be processed against; defaults to the original transaction's. */
  public selectedMonekId: string;

  constructor(
    private merchantService: MerchantService,
    public dialogRef: MatDialogRef<RechargeTransactionModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: RechargeModalData,
    private VTService: VTService,
    private notifcationService: NotificationService,
    private errorReference: ErrorReferenceService,
    private confirmationService: ConfirmationService,
    private currencyPipe: CurrencyPipe,
    private countryConvertService: CountryConvertService,
  ) {
    this.selectedMonekId = this.data.transaction.merchant?.monekId ?? '';
    this.rechargeForm = this.BuildForm();
  }

  public get currencyAlphaCode(): string {
    const countryData = this.countryConvertService.GetCountryByCurrencyNumericCode(
      this.data.transaction.currencyCode,
    );
    return countryData ? countryData.currencyCode : 'GBP';
  }

  public onAccountSelected(account: AccountOption): void {
    this.selectedMonekId = account.monekId;
    this.rechargeForm.formFields
      .find((f: FormField) => f.id === 'MonekID')
      ?.SetValue(account.monekId);
  }

  private BuildForm(): FormData<TransactionRecharge> {
    const userProfile = this.merchantService.getUserprofile();
    const currencyAlphaCode = this.currencyAlphaCode;
    const currencySymbol = getCurrencySymbol(currencyAlphaCode, 'narrow');
    const formData = new FormBuilder<TransactionRecharge>(MonekColour.Primary)
      .ToggleSubmitButton(true)
      .AddFormField('OperatorId', '', InputType.Hidden, userProfile?.userId || '', Size.Full)
      .AddFormField('OperatorName', '', InputType.Hidden, userProfile?.email || '', Size.Full)
      .SubmitButtonConfig('Charge customer', Size.Full, Alignment.Center)
      .AddFormField(
        'PaymentChannel',
        '',
        InputType.Hidden,
        this.data.transaction.channel,
        Size.Auto,
      )
      .AddFormField('AuxiliaryData', '', InputType.Hidden, '', Size.Auto)
      .AddFormField('MerchantID', '', InputType.Hidden, '', Size.Auto)
      .AddFormField('CountryCode', '', InputType.Hidden, this.data.transaction.countryCode, Size.Auto)
      .AddFormField('CurrencyCode', '', InputType.Hidden, this.data.transaction.currencyCode, Size.Auto)
      .AddFormField('ValidityID', '', InputType.Hidden, null, Size.Auto)
      .AddFormField(
        'CrossReference',
        '',
        InputType.Hidden,
        this.data.transaction.crossReference,
        Size.Auto,
      )
      .AddFormField('MonekID', '', InputType.Hidden, this.selectedMonekId, Size.Auto)
      .AddFormField(
        'Amount',
        `Amount to charge`,
        InputType.Number,
        null,
        Size.Full,
        new FormValidation()
          .Required('Amount is required')
          .MinValue(
            0.01,
            `Amount must be greater than ${this.currencyPipe.transform(
              0.01,
              currencyAlphaCode,
            )}`,
          )
          .MaxLength(9)
          .PatternMatch(ValidationPattern.Decimal, 'Amount must be a number'),
        true,
        {
          prefixIcon: new InputPrefixSuffixType(
            InputIconType.TEXT,
            currencySymbol,
          ),
        },
        undefined,
        (formfield: FormField, value: string) => {
          if (value !== '') {
            const numericValue = parseFloat(value);
            if (!isNaN(numericValue)) {
              formfield.value = numericValue.toFixed(2);
            } else {
              formfield.value = '0.00';
            }
          }
        },
      )
      .AddOptionField(
        'Reason',
        `Reason for charge`,
        OptionInputType.Dropdown,
        null,
        [
          new FormOption('A', 'Additional Charge'),
          new FormOption('C', 'Unscheduled Charge'),
          new FormOption('D', 'Delayed Charge'),
          new FormOption('L', 'Incremental Charge'),
          new FormOption('X', 'No Show'),
        ],
        Size.Full,
        new FormValidation().Required(),
        true,
        {},
        undefined,
      )
      .Build((data: TransactionRecharge) => {
        this.makeRecharge(data);
      });
    return formData;
  }

  private makeRecharge(data: TransactionRecharge): void {
    this.processing = true;

    const accountNote = this.selectedMonekId
      ? ` It will be processed on merchant account ${this.selectedMonekId}.`
      : '';

    this.confirmationService
      .ConfirmDecline(
        'Charge this customer?',
        `The card on file will be charged immediately.${accountNote}`,
        'Charge',
        'Cancel',
        'primary',
        'warn',
      )
      .subscribe((resp: boolean) => {
        if (resp) this.executeRecharge(data);
        else this.processing = false;
      });
  }
  /**
   * AMEX requires LID fields with recharge requests.
   */
  private withAmexLineItem(data: TransactionRecharge): TransactionRecharge {
    const paymentDetail = (this.data.transaction?.paymentDetail ?? '').trim();
    const amount = (data as unknown as { Amount?: number }).Amount ?? data.amount;
    return {
      ...data,
      LIDItem1Description: paymentDetail ? paymentDetail.substring(0, 15) : 'Goods',
      LIDItem1Quantity: 1,
      LIDItem1GrossValue: amount,
    };
  }

  private executeRecharge(request: TransactionRecharge): void {
    const data = this.withAmexLineItem(request);
    this.VTService.SubmitRechargeRequest(data, this.selectedMonekId).subscribe(
      (_resp: VTPaymentResponse) => {
        if (_resp.status) {
          this.dialogRef.close(data);
          this.notifcationService.SmallDialog(
            'Recharge completed',
            'Close',
            60000,
          );
        } else {
          const failure = new Error(_resp.message || 'Recharge failed');
          // Keep the server's trace id as the reference; a locally minted one would match no log line.
          this.errorReference.recordBackendReference(failure, _resp.traceId);
          this.rechargeForm.ShowError(this.errorReference.formatMessage(failure.message, failure));
        }
        this.processing = false;
      },
      (err: unknown) => {
        this.rechargeForm.ShowError(this.errorReference.formatMessage('Something went wrong.', err));
        this.processing = false;
      },
    );
  }

  // close without saving
  public cancel(): void {
    this.dialogRef.close(null);
  }
}
